import {
  ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE,
  ABBYCARE_TERMINOLOGY_CODES_BASE
} from 'fhir/constants'

export const ENCOUNTER_VISIT_NUMBER_CODE_SYSTEM = `${ABBYCARE_TERMINOLOGY_CODES_BASE}/encounter-visit-number`

export const ENCOUNTER_REASON_CODE_SYSTEM = `${ABBYCARE_TERMINOLOGY_CODES_BASE}/reason`

export enum EncounterStatus {
  PLANNED = 'planned',
  ARRIVED = 'arrived',
  TRIAGED = 'triaged',
  IN_PROGRESS = 'in-progress',
  ON_LEAVE = 'onleave',
  FINISHED = 'finished',
  INCOMPLETE = 'incomplete',
  CANCELLED = 'cancelled',
  ENTERED_IN_ERROR = 'entered-in-error'
}

export const AUTHORIZED_ENCOUNTER_STATUSES = [
  EncounterStatus.PLANNED,
  EncounterStatus.ARRIVED,
  EncounterStatus.TRIAGED,
  EncounterStatus.IN_PROGRESS,
  EncounterStatus.ON_LEAVE,
  EncounterStatus.FINISHED,
  EncounterStatus.INCOMPLETE,
  EncounterStatus.CANCELLED
]

export const pendingEccounterStatuses = [EncounterStatus.ON_LEAVE]
export const cancelledEncounterStatuses = [EncounterStatus.CANCELLED]
export const chartedEncounterStatuses = [EncounterStatus.FINISHED, EncounterStatus.INCOMPLETE]
export const chartableEncounterStatuses = [
  EncounterStatus.PLANNED,
  EncounterStatus.ARRIVED,
  EncounterStatus.TRIAGED,
  EncounterStatus.IN_PROGRESS
]

export const ENCOUNTER_PARTICIPANT_TYPE_CODE_SYSTEM = `${ABBYCARE_TERMINOLOGY_CODES_BASE}/encounter-participant-type`
export const BACKCHARTED_IDENTIFIER_SYSTEM = `${ABBYCARE_TERMINOLOGY_CODES_BASE}/backcharted`

export enum EncounterParticipantType {
  CAREGIVER = 'caregiver'
}

// Effective visit status displayed to user based on encounter, appointment, and task resources
export enum CalculatedVisitStatus {
  SCHEDULED = 'scheduled',
  SUBMITTED = 'submitted',
  MISSED = 'missed',
  LATE = 'late',
  APPROVED = 'approved',
  DENIED = 'denied',
  HOLD = 'on-hold',
  UNKNOWN = 'unknown'
}

// Maps to visit status in the API: api/services/visit/visit_services.py
export enum VisitStatus {
  SCHEDULED = 'scheduled',
  ON_HOLD = 'on-hold',
  MISSED = 'missed',
  DELETED = 'deleted',
  COMPLETED = 'completed'
}

export const CalculatedVisitStatusToVisitStatus = {
  [CalculatedVisitStatus.SCHEDULED]: VisitStatus.SCHEDULED,
  [CalculatedVisitStatus.HOLD]: VisitStatus.ON_HOLD,
  [CalculatedVisitStatus.MISSED]: VisitStatus.MISSED,
  [CalculatedVisitStatus.LATE]: VisitStatus.SCHEDULED,
  [CalculatedVisitStatus.APPROVED]: VisitStatus.COMPLETED,
  [CalculatedVisitStatus.DENIED]: VisitStatus.COMPLETED,
  [CalculatedVisitStatus.SUBMITTED]: VisitStatus.COMPLETED
}

export const ENCOUNTER_CLASS_CODE_SYSTEM = `${ABBYCARE_TERMINOLOGY_CODES_BASE}/encounter-class`

export enum EncounterClassCode {
  CNA_VISIT = 'cna-visit',
  HHA_STANDARD_SHIFT = 'hha-standard-shift',
  PCP_STANDARD_SHIFT = 'pcp-standard-shift',
  RPCP_STANDARD_SHIFT = 'rpcp-standard-shift',
  HMA_STANDARD_SHIFT = 'hma-standard-shift',
  HMK_STANDARD_SHIFT = 'hmk-standard-shift',
  LRP_HMK_STANDARD_SHIFT = 'lrp-hmk-standard-shift',
  RSP_STANDARD_SHIFT = 'rsp-standard-shift',
  SCC_STANDARD_SHIFT = 'scc-standard-shift',
  CC_STANDARD_SHIFT = 'cc-standard-shift',
  HMK_E_EC_STANDARD_SHIFT = 'hmk-e-ec-standard-shift',
  SFC_STANDARD_SHIFT = 'sfc-standard-shift',
  ATTC_STANDARD_SHIFT = 'attc-standard-shift',
  HCA_STANDARD_SHIFT = 'hca-standard-shift',
  GA_PSS_STANDARD_SHIFT = 'ga-pss-standard-shift'
}

export const STANDARD_SHIFT_ENCOUNTER_CLASS_CODES = [
  EncounterClassCode.HHA_STANDARD_SHIFT,
  EncounterClassCode.PCP_STANDARD_SHIFT,
  EncounterClassCode.RPCP_STANDARD_SHIFT,
  EncounterClassCode.HMA_STANDARD_SHIFT,
  EncounterClassCode.HMK_STANDARD_SHIFT,
  EncounterClassCode.LRP_HMK_STANDARD_SHIFT,
  EncounterClassCode.RSP_STANDARD_SHIFT,
  EncounterClassCode.SCC_STANDARD_SHIFT,
  EncounterClassCode.CC_STANDARD_SHIFT,
  EncounterClassCode.HMK_E_EC_STANDARD_SHIFT,
  EncounterClassCode.SFC_STANDARD_SHIFT,
  EncounterClassCode.ATTC_STANDARD_SHIFT,
  EncounterClassCode.HCA_STANDARD_SHIFT,
  EncounterClassCode.GA_PSS_STANDARD_SHIFT
]

export const EncounterClassCodeToServiceType: Record<EncounterClassCode, string> = {
  [EncounterClassCode.CNA_VISIT]: 'CNA',
  [EncounterClassCode.HHA_STANDARD_SHIFT]: 'HHA',
  [EncounterClassCode.PCP_STANDARD_SHIFT]: 'PCP',
  [EncounterClassCode.RPCP_STANDARD_SHIFT]: 'RPCP',
  [EncounterClassCode.HMA_STANDARD_SHIFT]: 'HMA',
  [EncounterClassCode.HMK_STANDARD_SHIFT]: 'HMK',
  [EncounterClassCode.LRP_HMK_STANDARD_SHIFT]: 'LRP-HMK',
  [EncounterClassCode.RSP_STANDARD_SHIFT]: 'RSP',
  [EncounterClassCode.SCC_STANDARD_SHIFT]: 'SCC',
  [EncounterClassCode.CC_STANDARD_SHIFT]: 'CC',
  [EncounterClassCode.HMK_E_EC_STANDARD_SHIFT]: 'HMK-E/EC',
  [EncounterClassCode.SFC_STANDARD_SHIFT]: 'SFC',
  [EncounterClassCode.ATTC_STANDARD_SHIFT]: 'ATTC',
  [EncounterClassCode.HCA_STANDARD_SHIFT]: 'HCA',
  // Combined with the patient state as `${state}-${abbrev}`, so this resolves to `GA-PSS`
  [EncounterClassCode.GA_PSS_STANDARD_SHIFT]: 'PSS'
}

export function getVisitName(classCode: EncounterClassCode | string | null | undefined): string {
  switch (classCode) {
    case EncounterClassCode.CNA_VISIT:
      return 'CNA Visit'
    case EncounterClassCode.HHA_STANDARD_SHIFT:
      return 'HHA Shift'
    case EncounterClassCode.PCP_STANDARD_SHIFT:
      return 'PCP Shift'
    case EncounterClassCode.RPCP_STANDARD_SHIFT:
      return 'RPCP Shift'
    case EncounterClassCode.HMA_STANDARD_SHIFT:
      return 'HMA Shift'
    case EncounterClassCode.HMK_STANDARD_SHIFT:
      return 'HMK Shift'
    case EncounterClassCode.LRP_HMK_STANDARD_SHIFT:
      return 'LRP-HMK Shift'
    case EncounterClassCode.RSP_STANDARD_SHIFT:
      return 'RSP Shift'
    case EncounterClassCode.SCC_STANDARD_SHIFT:
      return 'SCC Shift'
    case EncounterClassCode.CC_STANDARD_SHIFT:
      return 'CC Shift'
    case EncounterClassCode.HMK_E_EC_STANDARD_SHIFT:
      return 'HMK-E/EC Shift'
    case EncounterClassCode.SFC_STANDARD_SHIFT:
      return 'SFC Shift'
    case EncounterClassCode.ATTC_STANDARD_SHIFT:
      return 'ATTC Standard Shift'
    case EncounterClassCode.HCA_STANDARD_SHIFT:
      return 'HCA Standard Shift'
    case EncounterClassCode.GA_PSS_STANDARD_SHIFT:
      return 'GA-PSS Standard Shift'
    default:
      return 'Visit'
  }
}

const ENCOUNTER_EVV_STATUS_LOCATION = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-evv-status-location`
const ENCOUNTER_EVV_STATUS_TIME = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-evv-status-time`
const ENCOUNTER_EVV_STATUS_SIGNATURE = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-evv-status-signature`
const ENCOUNTER_EVV_STATUS_TASK_COMPLETION = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-evv-status-task-completion`

export const ENCOUNTER_STATUS_CHANGE_REASON_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-status-history-reason`
export const ENCOUNTER_STATUS_CHANGE_REASON_NOTES_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-status-history-reason-notes`
export const ENCOUNTER_STATUS_CHANGE_PRACTITIONER_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-status-history-practitioner`

export const ENCOUNTER_REASON_FOR_DELAYED_CHARTING = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/reason-for-delayed-charting`
export const ENCOUNTER_BETTER_SUPPORT = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/feedback-for-better-support`

export const ENCOUNTER_PATIENT_SIGNATURE_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-patient-signature`
export const ENCOUNTER_NOTE_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-note`
export const ENCOUNTER_SIGNATURE_FORMAT_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-signature-format`

export enum EncounterEVVExtensionTypes {
  TIME = 'time',
  SIGNATURE = 'signature',
  TASK_COMPLETION = 'task-completion'
}

export type EncounterEVVExtensions =
  | typeof ENCOUNTER_EVV_STATUS_LOCATION
  | typeof ENCOUNTER_EVV_STATUS_TIME
  | typeof ENCOUNTER_EVV_STATUS_SIGNATURE
  | typeof ENCOUNTER_EVV_STATUS_TASK_COMPLETION

export type EncounterStatusHistoryExtensions =
  | typeof ENCOUNTER_STATUS_CHANGE_REASON_EXTENSION_URL
  | typeof ENCOUNTER_STATUS_CHANGE_REASON_NOTES_EXTENSION_URL

export const EncounterEVVExtensionsMap: Record<EncounterEVVExtensionTypes, EncounterEVVExtensions> =
  {
    [EncounterEVVExtensionTypes.TIME]: ENCOUNTER_EVV_STATUS_TIME,
    [EncounterEVVExtensionTypes.SIGNATURE]: ENCOUNTER_EVV_STATUS_SIGNATURE,
    [EncounterEVVExtensionTypes.TASK_COMPLETION]: ENCOUNTER_EVV_STATUS_TASK_COMPLETION
  }

export enum CNAVisitEncounterEVVStatus {
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  INDETERMINATE = 'indeterminate'
}

export const ENCOUNTER_CARE_PLAN_EXTENSION_URL = `${ABBYCARE_FHIR_STRUCTURE_DEFINITION_BASE}/encounter-care-plan`

export enum EncounterStatusChangeReason {
  PATIENT_WITH_OTHER_CAREGIVER = 'patient-with-other-caregiver',
  CAREGIVER_UNAVAILABLE = 'caregiver-unavailable',
  HOSPITALIZATION = 'hospitalization',
  INVALID_INSURANCE = 'invalid-insurance',
  ER_VISIT = 'er-visit',
  PATIENT_APPOINTMENT_CONFLICT = 'patient-appointment-conflict',
  PATIENT_SCHOOL_HOURS_CONFLICT = 'patient-school-hours-conflict',
  CHARTING_WINDOW_EXPIRED = 'charting-window-expired',
  OTHER = 'other',
  OUTSIDE_OF_AUTHORIZATION_PERIOD = 'outside-of-authorization-period',
  VISIT_QA_APPROVED = 'visit-qa-approved',
  VISIT_QA_DENIED = 'visit-qa-denied',
  VISIT_INCOMPLETE = 'visit-incomplete',
  MISSED_VISIT_QA_COMPLETED = 'missed-visit-qa-completed',
  VISIT_COMPLETED = 'visit-completed',
  BACKCHARTING = 'backcharting',

  // Generic reason for marking a visit as on hold
  HOLD_CREATED = 'hold-created',

  // Bulk schedule edit applied across matching visits
  BULK_SCHEDULE_UPDATED = 'bulk-schedule-updated'
}

export const EncounterStatusChangeReasonToDisplay: Record<EncounterStatusChangeReason, string> = {
  [EncounterStatusChangeReason.PATIENT_WITH_OTHER_CAREGIVER]: 'Patient with other parent/guardian',
  [EncounterStatusChangeReason.CAREGIVER_UNAVAILABLE]: 'Caregiver unavailable',
  [EncounterStatusChangeReason.HOSPITALIZATION]: 'Hospitalization (admitted)',
  [EncounterStatusChangeReason.ER_VISIT]: 'ER Visit',
  [EncounterStatusChangeReason.PATIENT_APPOINTMENT_CONFLICT]:
    'Patient conflict with medical appointment',
  [EncounterStatusChangeReason.PATIENT_SCHOOL_HOURS_CONFLICT]: 'Patient conflict with school hours',
  [EncounterStatusChangeReason.CHARTING_WINDOW_EXPIRED]: 'Charting window expired',
  [EncounterStatusChangeReason.INVALID_INSURANCE]: 'Invalid insurance',
  [EncounterStatusChangeReason.OTHER]: 'Other',
  [EncounterStatusChangeReason.OUTSIDE_OF_AUTHORIZATION_PERIOD]: 'Outside of authorization period',
  [EncounterStatusChangeReason.VISIT_QA_APPROVED]: 'Visit QA Approved',
  [EncounterStatusChangeReason.VISIT_QA_DENIED]: 'Visit QA Denied',
  [EncounterStatusChangeReason.VISIT_INCOMPLETE]: 'Incomplete visit',
  [EncounterStatusChangeReason.MISSED_VISIT_QA_COMPLETED]: 'Missed Visit QA Completed',
  [EncounterStatusChangeReason.VISIT_COMPLETED]: 'Visit Completed',
  [EncounterStatusChangeReason.BACKCHARTING]: 'Backcharting',
  [EncounterStatusChangeReason.HOLD_CREATED]: 'Hold Created',
  [EncounterStatusChangeReason.BULK_SCHEDULE_UPDATED]: 'Bulk schedule update'
}
